import { useCallback, KeyboardEvent, MouseEvent, useRef } from 'react';
import { ThumbnailItemClickProps, ThumbnailKeyDownProps } from '../components';
import { GridGroup } from '../types';

export function useGridKeyboardNavigation(
  groups: GridGroup[],
  numberOfColumns: number,
  handleSelectPage: (id: string, e: KeyboardEvent | MouseEvent) => void,
  onThumbnailItemClick?: (props: ThumbnailItemClickProps) => void,
) {
  const focusIndexRef = useRef<number>(-1);

  // Mirrored into refs so onThumbnailKeyDown keeps a stable identity: it is
  // handed to every thumbnail in the grid, and a new identity on each reorder
  // would re-render all of them.
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const numberOfColumnsRef = useRef(numberOfColumns);
  numberOfColumnsRef.current = numberOfColumns;

  const handleSelectPageRef = useRef(handleSelectPage);
  handleSelectPageRef.current = handleSelectPage;

  const onThumbnailItemClickRef = useRef(onThumbnailItemClick);
  onThumbnailItemClickRef.current = onThumbnailItemClick;

  // Helper function to find the group and local index of an item by its global index
  const findGroupAndLocalIndex = useCallback((globalIdx: number) => {
    let cumulativeItems = 0;
    for (const group of groupsRef.current) {
      if (globalIdx < cumulativeItems + group.items.length) {
        return {
          group,
          localIdx: globalIdx - cumulativeItems,
          groupStartIdx: cumulativeItems,
        };
      }
      cumulativeItems += group.items.length;
    }
    return null;
  }, []);

  // Helper function to get the global index from group and local index
  const getGlobalIndex = useCallback((group: GridGroup, localIdx: number) => {
    let cumulativeItems = 0;
    for (const g of groupsRef.current) {
      if (g.id === group.id) {
        return cumulativeItems + localIdx;
      }
      cumulativeItems += g.items.length;
    }
    return -1;
  }, []);

  const onThumbnailKeyDown = useCallback(
    ({ event, pageIndex, pageId }: ThumbnailKeyDownProps) => {
      const groups = groupsRef.current;
      const numberOfColumns = numberOfColumnsRef.current;
      const allItemIds = groups.flatMap((g) => g.items.map((i) => i.id));
      const globalIdx = allItemIds.indexOf(pageId);
      if (globalIdx === -1) return;

      const current = findGroupAndLocalIndex(globalIdx);
      if (!current) return;
      const { group, localIdx, groupStartIdx } = current;

      let nextGlobalIdx = globalIdx;
      let nextId: string | null = null;

      if (event.key === 'ArrowRight') {
        // Move to the next item in the same row if possible
        const rowIdx = Math.floor(localIdx / numberOfColumns);
        const colIdx = localIdx % numberOfColumns;
        if (colIdx < numberOfColumns - 1 && localIdx + 1 < group.items.length) {
          nextGlobalIdx = globalIdx + 1;
          nextId = group.items[localIdx + 1].id;
        } else if (localIdx + 1 < group.items.length) {
          // Move to the first item in the next row
          nextGlobalIdx = groupStartIdx + (rowIdx + 1) * numberOfColumns;
          nextId = group.items[(rowIdx + 1) * numberOfColumns]?.id;
        } else {
          // Move to the first item of the next group
          const currentGroupIdx = groups.findIndex((g) => g.id === group.id);
          if (currentGroupIdx < groups.length - 1) {
            const nextGroup = groups[currentGroupIdx + 1];
            const nextLocalIdx = 0;
            nextGlobalIdx = getGlobalIndex(nextGroup, nextLocalIdx);
            nextId = nextGroup.items[nextLocalIdx]?.id;
          }
        }
      } else if (event.key === 'ArrowLeft') {
        // Move to the previous item in the same row if possible
        const rowIdx = Math.floor(localIdx / numberOfColumns);
        const colIdx = localIdx % numberOfColumns;
        if (colIdx > 0) {
          nextGlobalIdx = globalIdx - 1;
          nextId = group.items[localIdx - 1].id;
        } else if (rowIdx > 0) {
          // Move to the last item in the previous row
          nextGlobalIdx = groupStartIdx + rowIdx * numberOfColumns - 1;
          nextId = group.items[rowIdx * numberOfColumns - 1]?.id;
        } else {
          // Move to the last item of the previous group
          const currentGroupIdx = groups.findIndex((g) => g.id === group.id);
          if (currentGroupIdx > 0) {
            const prevGroup = groups[currentGroupIdx - 1];
            const nextLocalIdx = prevGroup.items.length - 1;
            nextGlobalIdx = getGlobalIndex(prevGroup, nextLocalIdx);
            nextId = prevGroup.items[nextLocalIdx]?.id;
          }
        }
      } else if (event.key === 'ArrowDown') {
        // Move to the item in the same column in the next row
        const rowIdx = Math.floor(localIdx / numberOfColumns);
        const colIdx = localIdx % numberOfColumns;
        const nextLocalIdx = (rowIdx + 1) * numberOfColumns + colIdx;
        if (nextLocalIdx < group.items.length) {
          nextGlobalIdx = groupStartIdx + nextLocalIdx;
          nextId = group.items[nextLocalIdx].id;
        } else {
          // Move to the next group, same column position if possible
          const currentGroupIdx = groups.findIndex((g) => g.id === group.id);
          if (currentGroupIdx < groups.length - 1) {
            const nextGroup = groups[currentGroupIdx + 1];
            const nextLocalIdx = Math.min(colIdx, nextGroup.items.length - 1);
            nextGlobalIdx = getGlobalIndex(nextGroup, nextLocalIdx);
            nextId = nextGroup.items[nextLocalIdx]?.id;
          }
        }
      } else if (event.key === 'ArrowUp') {
        // Move to the item in the same column in the previous row
        const rowIdx = Math.floor(localIdx / numberOfColumns);
        const colIdx = localIdx % numberOfColumns;
        if (rowIdx > 0) {
          const nextLocalIdx = (rowIdx - 1) * numberOfColumns + colIdx;
          nextGlobalIdx = groupStartIdx + nextLocalIdx;
          nextId = group.items[nextLocalIdx].id;
        } else {
          // Move to the previous group, same column position if possible
          const currentGroupIdx = groups.findIndex((g) => g.id === group.id);
          if (currentGroupIdx > 0) {
            const prevGroup = groups[currentGroupIdx - 1];
            const prevGroupRows = Math.ceil(prevGroup.items.length / numberOfColumns);
            const nextLocalIdx = Math.min((prevGroupRows - 1) * numberOfColumns + colIdx, prevGroup.items.length - 1);
            nextGlobalIdx = getGlobalIndex(prevGroup, nextLocalIdx);
            nextId = prevGroup.items[nextLocalIdx]?.id;
          }
        }
      } else if (event.key === ' ') {
        handleSelectPageRef.current(pageId, event);
        event.preventDefault();
        return;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        handleSelectPageRef.current(pageId, event);
        if (onThumbnailItemClickRef.current && pageIndex >= 0) {
          onThumbnailItemClickRef.current({ pageIndex: pageIndex });
        }
        return;
      } else {
        return;
      }

      if (nextId && nextGlobalIdx !== globalIdx) {
        const el = document.querySelector(`[data-grid-keyboard-navigation-item-id="${nextId}"]`) as HTMLElement | null;
        if (el) {
          el.focus();
        }
        focusIndexRef.current = nextGlobalIdx;
        event.preventDefault();
      }
    },
    [findGroupAndLocalIndex, getGlobalIndex],
  );

  return { onThumbnailKeyDown };
}
